//@author: mandy

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
} from "@angular/core";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { jSith } from "../../util/jquery-replacement";
import { S25ImageService } from "./s25.image.service";
import { LangService } from "../../services/lang.service";
import { S25LoadingApi } from "../s25-loading/loading.api";
import { ImageService } from "../../services/image.service";
import { LoginService } from "../../services/login.service";
import { Image } from "../../pojo/Image";

export interface ImageModelI {
    imageId: number | undefined;
    title?: string;
    description?: string;
    imageUrl?: string;
    imageUrlWidth?: string;
    bootstrap?: Image.BootstrapOptions;
    fileType?: string;

    autoSaveUpload?: boolean;
    action?: Function;
    /** Base64 image data string, or a numeric imageId for deferred loading. */
    data?: string | number;
}
@TypeManagerDecorator("s25-ng-image")
@Component({
    selector: "s25-ng-image",
    template: `
        <s25-loading-inline></s25-loading-inline>
        @if (isPromiseResolved) {
            <div class="s25-image s25-image-body">
                @if (isError) {
                    <div class="s25-error">{{ lang.image_load_error }}</div>
                }
                @if (!isError && bean) {
                    <div>
                        <!--no link if the objectId isn't available-->
                        @if (!!bean.imageId) {
                            <a [href]="bean.imageUrl" target="_blank" rel="noopener" class="s25-image-object">
                                <img
                                    [src]="imageObject.imageUrl"
                                    [alt]="bean.description || bean.title"
                                    [title]="imageObject.title"
                                    (error)="onError()"
                                    (load)="onLoad()"
                                />
                            </a>
                        }
                        @if (!bean.imageId) {
                            <img
                                [src]="imageObject.imageUrl"
                                [alt]="bean.description || bean.title"
                                [title]="imageObject.title"
                                (error)="onError()"
                                (load)="onLoad()"
                                class="s25-image-object"
                            />
                        }
                    </div>
                }
            </div>
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class S25ImageComponent implements OnInit, OnChanges {
    //S25ImageService_BootstrapDefault {point: 'sm', numCols: 3}
    //model: {imageId: Integer, title: String, bootstrap: {point: String, numCols: Integer}}
    @Input() model: ImageModelI;

    bean: any;
    lang: any;
    loadedWidth: any;
    isPromiseResolved: boolean = false;
    imageObject: { imageUrl: string; title: string };
    isError: boolean = false;
    isLoggedIn: boolean;
    private imageData: Image.ImageFullI | undefined;

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.model && this.isPromiseResolved) {
            this.init(this.model);
        }
    }

    ngOnInit() {
        this.elementRef.nativeElement.angBridge = this;

        if (this.model) {
            S25LoadingApi.init(this.elementRef.nativeElement);
            jSith.addClass(this.elementRef.nativeElement, "s25-image");

            this.cd.detectChanges();
            this.init(this.model);
        }
    }

    init(model: any) {
        return S25Util.all({
            bean: this.createBean(model),
            lang: LangService.getLang(),
            // This will always return false. TODO: update the cache or use a different mechanism
            loadedWidth: S25ImageService.getLoadedBootstrapWidth(model.imageId, model.bootstrap),
            isLoggedIn: LoginService.getLoggedIn(),
        }).then((response) => {
            this.isPromiseResolved = true;
            this.loadedWidth = response.loadedWidth;
            this.lang = response.lang.div.application;
            this.bean = response.bean;
            this.imageObject = this.createImageObjectModel(this.bean);
            this.isLoggedIn = response.isLoggedIn;

            this.cd.detectChanges();
        });
    }

    createImageObjectModel(bean: any) {
        let url: string = "";
        if (this.model.imageUrl) url = this.model.imageUrl;
        else if (!url && bean.imageId) url = bean.imageUrlWidth;
        else url = S25Util.toBase64URI(bean.data, bean.fileType);

        return {
            imageUrl: url,
            title: bean.title,
        };
    }

    onLoad() {
        if (!this.isLoggedIn) {
            S25LoadingApi.destroy(this.elementRef.nativeElement);
            this.cd.detectChanges();
            return;
        }

        return this.getUrl(this.model.imageId)
            .then((data) => {
                this.imageObject.imageUrl = data ?? "";
                this.cd.markForCheck();
                S25LoadingApi.destroy(this.elementRef.nativeElement);
            })
            .catch((error) => {
                S25Util.showError(error);
                S25LoadingApi.destroy(this.elementRef.nativeElement);
            });
    }

    onError() {
        setTimeout(() => {
            S25LoadingApi.destroy(this.elementRef.nativeElement);
            this.isError = true;
            this.cd.detectChanges();
        }, 1);
    }

    createBean(model: any) {
        return S25Util.all({
            imageData: model.imageId
                ? ImageService.getImage(model.imageId).catch((): undefined => undefined)
                : undefined,
            imageUrlWidth: S25ImageService.getUrlBootstrapWidth(model.imageId, model.bootstrap),
            imageUrl: this.model.imageUrl || S25ImageService.getUrl(model.imageId),
        }).then((response) => {
            this.imageData = response.imageData; // cache for reuse in getUrl
            S25Util.extend(model, {
                description: Image.altText(response.imageData) || model.description || "",
                title: response.imageData?.image_name?.trim() || model.title || "",
                imageUrlWidth: response.imageUrlWidth,
                imageUrl: response.imageUrl,
            });
            return model;
        });
    }

    async getUrl(itemId: number | undefined) {
        if (this.model.imageUrl) return this.model.imageUrl;
        if (!itemId) return "";

        const prevUrl = this.imageObject.imageUrl.split("&");
        const resp = this.imageData ?? (await ImageService.getImage(itemId));
        if (!resp?.file_name) return this.imageObject.imageUrl;

        let formattedName: string | undefined;
        for (let i = resp.file_name.length - 1; i >= 0; i--) {
            if (resp.file_name[i] === "\\") {
                formattedName = resp.file_name.slice(i + 1);
                break;
            }
        }

        if (prevUrl.length > 0) {
            prevUrl[1] = `filename=${formattedName ?? resp.file_name}`;

            return prevUrl.slice(0, 2).join("&");
        }
    }
}
